import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/Card';
import Button from '../components/ui/Button';
import PageHeader from '../components/PageHeader';
import payrollMappingService, {
  type PayrollCategoryMapping,
  type PayrollExportTarget,
} from '../services/payrollMappingService';
import xeroPayrollService from '../services/xeroPayroll';
import { useModuleLicense } from '../hooks/useModuleLicense';
import { Link } from 'react-router-dom';
import { FileText, RefreshCw } from 'lucide-react';

const TARGET_LABELS: Record<PayrollExportTarget, string> = {
  myob: 'MYOB',
  xero: 'Xero',
};

const TARGET_DESCRIPTIONS: Record<PayrollExportTarget, string> = {
  myob: 'Names written to the "Payroll Category" column of the MYOB tab-separated export.',
  xero: 'Earnings rates read live from your connected Xero organisation. Picking one stores its ID (required by the Payroll API) alongside its name (used by the CSV export).',
};

/** An external payroll item that can be picked rather than typed. */
interface ExternalOption {
  id: string;
  name: string;
}

interface TargetPanelProps {
  orgId: number;
  target: PayrollExportTarget;
}

function TargetPanel({ orgId, target }: TargetPanelProps) {
  const [rows, setRows] = useState<PayrollCategoryMapping[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [options, setOptions] = useState<ExternalOption[] | null>(null);
  const [optionsNote, setOptionsNote] = useState<string | null>(null);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [reload, setReload] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await payrollMappingService.getMappings(orgId, target);
      if (response.success && response.data) {
        // Ensure export_enabled defaults to true for any rows that don't have it
        setRows(response.data.map(r => ({ ...r, export_enabled: r.export_enabled ?? true })));
      } else {
        setError((response as { error?: { message?: string } }).error?.message || 'Failed to load mappings');
      }
    } catch {
      setError('Failed to load mappings');
    } finally {
      setLoading(false);
    }
  }, [orgId, target]);

  useEffect(() => { load(); }, [load]);

  // Non-null once there is a live list to pick from. Stays null for MYOB, which
  // matches by name, and whenever Xero can't be reached — in that case the panel
  // falls back to free text so the existing CSV workflow keeps working.
  // `reload` re-reads the list for a rate added in Xero while the page is open.
  useEffect(() => {
    if (target !== 'xero') {
      setOptions(null);
      setOptionsNote(null);
      return;
    }
    let cancelled = false;
    setOptionsLoading(true);
    (async () => {
      try {
        const response = await xeroPayrollService.listEarningsRates();
        if (cancelled) return;
        if (response.success && response.data) {
          setOptions(response.data.map(r => ({ id: r.earnings_rate_id, name: r.name })));
          setOptionsNote(null);
        } else {
          setOptions(null);
          setOptionsNote('Could not read earnings rates from Xero — enter names manually for now.');
        }
      } catch (e: unknown) {
        if (cancelled) return;
        setOptions(null);
        // The server says which step is missing (not connected, no organisation
        // chosen, connection expired) — that beats a generic guess.
        const reason = e instanceof Error && e.message ? e.message : 'Xero is not connected.';
        setOptionsNote(`${reason} Enter names manually, or set up Xero under HR → Xero Payroll Integration to pick from a list.`);
      } finally {
        if (!cancelled) setOptionsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [target, reload]);

  // Keeps id and name in step. The API push needs the id and the CSV export needs
  // the name; letting the two drift is exactly how a rename in Xero breaks payroll
  // silently.
  const selectExternal = (internal: string, id: string) => {
    const option = options?.find(o => o.id === id) ?? null;
    setRows(prev => prev.map(r =>
      r.internal_category === internal
        ? { ...r, external_id: option?.id ?? null, external_name: option?.name ?? null }
        : r
    ));
  };

  // Free-text fallback only. Deliberately leaves external_id alone: if Xero is
  // briefly unreachable we must not wipe a good id, and the id is authoritative
  // for the API push regardless of what is typed here.
  const updateExternalName = (internal: string, value: string) => {
    setRows(prev => prev.map(r =>
      r.internal_category === internal ? { ...r, external_name: value } : r
    ));
  };

  const toggleExportEnabled = (internal: string, checked: boolean) => {
    setRows(prev => prev.map(r =>
      r.internal_category === internal ? { ...r, export_enabled: checked } : r
    ));
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const response = await payrollMappingService.updateMappings(orgId, target, rows);
      if (response.success && response.data) {
        setRows(response.data.map(r => ({ ...r, export_enabled: r.export_enabled ?? true })));
        setSuccess('Saved successfully');
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError((response as { error?: { message?: string } }).error?.message || 'Failed to save');
      }
    } catch {
      setError('Failed to save mappings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{TARGET_LABELS[target]} Export Mappings</CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-gray-600 mb-4">{TARGET_DESCRIPTIONS[target]}</p>
        <p className="text-sm text-gray-600 mb-4">
          Uncheck <strong>Export</strong> to keep a category calculated internally without
          sending it to {TARGET_LABELS[target]} (e.g. Time in Lieu managed in-house).
          Setting an exported category back to <em>(not mapped)</em> removes its mapping —
          the export will be blocked until one is set.
        </p>
        {target === 'xero' && (
          <div className="text-sm text-blue-900 bg-blue-50 border border-blue-200 rounded px-3 py-2 mb-4">
            <p>
              These lists hold Xero <strong>earnings rates</strong> only. Leave is sent to Xero as
              leave applications, so a Xero <strong>leave type</strong> is not listed here. Choose
              leave types under Step 5 on{' '}
              <Link to="/accounts/xero" className="underline font-medium">
                Xero Payroll settings
              </Link>.
            </p>
            <Button
              variant="ghost"
              onClick={() => setReload(n => n + 1)}
              disabled={optionsLoading}
              className="mt-1 flex items-center gap-1"
            >
              <RefreshCw className="h-4 w-4" />
              {optionsLoading ? 'Reading from Xero…' : 'Reload from Xero'}
            </Button>
          </div>
        )}
        {optionsNote && (
          <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2 mb-4">
            {optionsNote}
          </p>
        )}

        {loading ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 pr-4 font-medium text-gray-700">Internal Category</th>
                  <th className="text-left py-2 pr-4 font-medium text-gray-700">
                    {TARGET_LABELS[target]} Name
                  </th>
                  <th className="text-center py-2 font-medium text-gray-700 w-20">Export</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(row => {
                  // A stored id the target no longer returns — the rate was deleted, or
                  // the connection now points at a different Xero organisation. Showing
                  // it beats letting the next pay run fail on a dangling reference.
                  const stale = !!row.external_id && !!options
                    && !options.some(o => o.id === row.external_id);
                  return (
                    <tr key={row.internal_category} className="border-b last:border-0">
                      <td className="py-2 pr-4 font-medium text-gray-800">
                        {row.internal_category}
                      </td>
                      <td className="py-2 pr-4">
                        {options ? (
                          <>
                            <select
                              value={row.external_id ?? ''}
                              disabled={!row.export_enabled}
                              onChange={e => selectExternal(row.internal_category, e.target.value)}
                              className={`w-full max-w-md border rounded px-2 py-1 text-sm transition-colors ${
                                !row.export_enabled
                                  ? 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed'
                                  : stale
                                    ? 'border-red-400 bg-white text-red-700'
                                    : 'border-gray-300 bg-white text-gray-900'
                              }`}
                            >
                              <option value="">
                                {row.export_enabled ? '(not mapped)' : '(internal only)'}
                              </option>
                              {stale && (
                                <option value={row.external_id ?? ''}>
                                  {row.external_name || 'Unknown rate'} — no longer in Xero
                                </option>
                              )}
                              {options.map(option => (
                                <option key={option.id} value={option.id}>{option.name}</option>
                              ))}
                            </select>
                            {stale && row.export_enabled && (
                              <p className="mt-1 text-xs text-red-600">
                                This earnings rate no longer exists in Xero. Choose a current
                                one before the next pay run.
                              </p>
                            )}
                          </>
                        ) : (
                          <input
                            type="text"
                            value={row.external_name ?? ''}
                            placeholder={row.export_enabled ? '(not mapped)' : '(internal only)'}
                            disabled={!row.export_enabled}
                            onChange={e => updateExternalName(row.internal_category, e.target.value)}
                            className={`w-full max-w-md border rounded px-2 py-1 text-sm transition-colors ${
                              row.export_enabled
                                ? 'border-gray-300 bg-white text-gray-900'
                                : 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed'
                            }`}
                          />
                        )}
                      </td>
                      <td className="py-2 text-center">
                        <input
                          type="checkbox"
                          checked={row.export_enabled}
                          onChange={e => toggleExportEnabled(row.internal_category, e.target.checked)}
                          className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
        {success && <p className="mt-3 text-sm text-green-600">{success}</p>}

        <div className="mt-4">
          <Button onClick={save} disabled={saving || loading}>
            {saving ? 'Saving…' : `Save ${TARGET_LABELS[target]} Mappings`}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default function PayrollExportMappings() {
  const { user } = useAuth();
  const orgId = user?.organisation_id;
  // The mappings sit with the connections they feed, in the Accounts module.
  // Any accounts seat reads them; the admin seat saves them.
  const { hasAccess: hasHRAccess, loading: hrLoading } = useModuleLicense('accounts', ['user', 'admin']);
  const { hasAccess: isAccountsAdmin } = useModuleLicense('accounts', 'admin');

  if (hrLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-neutral-500">Loading…</p>
      </div>
    );
  }

  if (!hasHRAccess) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <FileText className="h-12 w-12 text-neutral-300 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-neutral-700">Access Restricted</h2>
          <p className="text-sm text-neutral-500 mt-1">An Accounts licence seat is required to access payroll mappings.</p>
        </div>
      </div>
    );
  }

  if (!orgId) {
    return (
      <>
        <PageHeader title="Payroll Export Mappings" />
        <div className="max-w-4xl mx-auto p-4">
          <p className="text-sm text-gray-500">You must belong to an organisation to configure payroll mappings.</p>
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader title="Payroll Export Mappings" />
      <div className="max-w-4xl mx-auto p-4 space-y-6">
        <p className="text-sm text-gray-600">
          Configure how each internal payroll category is written to your MYOB and Xero
          payroll exports. Uncheck <strong>Export</strong> on any category your company
          handles internally — it will still be calculated in the system but excluded from
          the export file. Categories left checked but with no name set will block the
          export until configured.
        </p>

        {!isAccountsAdmin && (
          <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-900 break-words">
            You can read this page. An Accounts Admin makes changes to it.
          </div>
        )}
        <fieldset disabled={!isAccountsAdmin} className="space-y-6 min-w-0 border-0 p-0 m-0">
          <TargetPanel orgId={orgId} target="myob" />
          <TargetPanel orgId={orgId} target="xero" />
        </fieldset>
      </div>
    </>
  );
}
